/**
 * What can be selected *together* (spec 2026-09-06-01 §1, §4).
 *
 * A group edit only means something when the fields on screen mean the same
 * thing for every member, so the editor never mixes kinds. The granularity of
 * "the same thing" is the **category** — and the categories are exactly the
 * layer panel's groups, because that panel is where the user reads the
 * drawing's structure: Unit, Space, Walls, Equipments, and openings split into
 * doors and windows (a door and a window share neither their fields nor their
 * symbol).
 *
 * Equipment of different types *are* one category: a radiator and a boiler
 * both have a footprint, a rotation and a parent, and moving, nudging,
 * deleting or re-parenting them together is the whole point. What they do not
 * share — the type itself, and the size presets remembered per type — the
 * inspector edits on the primary only.
 *
 * Pure TypeScript: no DOM, no React, unit-tested with vitest.
 */
import type { BBox, Point } from './geometry'
import { equipmentFootprintCorners, type LayerId } from './render'
import { isGrouperElement, type EditorElement, type SceneDoc } from './scene'

/** One selectable family. `walls` keeps the layer's plural for symmetry. */
export type SelectionCategory = 'unit' | 'space' | 'walls' | 'equipment' | 'door' | 'window' | 'runs' | 'risers'

/**
 * The marquee's tie-break when two categories have as many candidates inside
 * the rectangle (spec 2026-09-06-01 §4). It reads unit-before-space like the
 * layer panel's spaces group, but it is its own order, not the panel's render
 * order — the panel draws walls first (GROUPS in LayerPanel.tsx).
 */
export const CATEGORY_ORDER: SelectionCategory[] = ['unit', 'space', 'walls', 'equipment', 'door', 'window', 'runs', 'risers']

/** Which family this element belongs to. The one place the rule lives. */
export function selectionCategory(el: EditorElement): SelectionCategory {
    switch (el.kind) {
        case 'wall':
            return 'walls'
        case 'space':
            return isGrouperElement(el) ? 'unit' : 'space'
        case 'equipment':
            return 'equipment'
        case 'opening':
            return el.openingKind === 'window' ? 'window' : 'door'
        case 'run':
            return 'runs'
        case 'riser':
            return 'risers'
    }
}

/** The layer whose visibility switch hides a category. */
export function categoryLayer(category: SelectionCategory): LayerId {
    switch (category) {
        case 'unit':
        case 'space':
            return 'spaces'
        case 'walls':
            return 'walls'
        case 'equipment':
            return 'equipment'
        case 'runs':
        case 'risers':
            // Risers share the networks layer with the runs of their family.
            return 'runs'
        default:
            return 'openings'
    }
}

/** Can these two be worked on together? */
export const sameCategory = (a: EditorElement, b: EditorElement): boolean =>
    selectionCategory(a) === selectionCategory(b)

/**
 * The points a marquee has to swallow whole for the element to count as inside
 * — the geometry the user sees, not a centroid: a wall whose far end sticks
 * out of the rectangle was not "inside" it by any reading.
 */
export function containmentPoints(el: EditorElement, symbols: SceneDoc['symbols']): Point[] {
    switch (el.kind) {
        case 'wall':
            return [el.start, el.end]
        case 'space':
            return el.boundary
        case 'equipment': {
            const symbol = el.typeCode ? symbols[el.typeCode] : undefined
            return equipmentFootprintCorners(el, symbol)
        }
        case 'opening': {
            const rad = (el.directionDeg * Math.PI) / 180
            const hw = el.width / 2
            return [
                { x: el.center.x - Math.cos(rad) * hw, y: el.center.y - Math.sin(rad) * hw },
                { x: el.center.x + Math.cos(rad) * hw, y: el.center.y + Math.sin(rad) * hw },
            ]
        }
        case 'run':
            return el.points
        case 'riser':
            return [el.position]
    }
}

/**
 * Fully-inside containment (spec §4). "Touching" — partial overlap — is
 * deliberately not offered: with a category rule on top of it, a marquee that
 * grabbed everything it brushed would make a wrong selection the default and a
 * right one a matter of aim.
 */
export function elementInsideBox(el: EditorElement, box: BBox, symbols: SceneDoc['symbols']): boolean {
    const pts = containmentPoints(el, symbols)
    if (!pts.length) return false
    return pts.every((p) => p.x >= box.x0 && p.x <= box.x1 && p.y >= box.y0 && p.y <= box.y1)
}

/**
 * The category a marquee with **no** prior selection settles on: the one with
 * the most candidates inside the rectangle, ties broken in layer-panel order.
 * Dragging a box over a floor is meant to catch its rooms, not the one wall
 * that happened to fit entirely inside it.
 */
export function dominantCategory(elements: EditorElement[]): SelectionCategory | null {
    const counts = new Map<SelectionCategory, number>()
    for (const el of elements) {
        const cat = selectionCategory(el)
        counts.set(cat, (counts.get(cat) ?? 0) + 1)
    }
    let best: SelectionCategory | null = null
    let bestCount = 0
    for (const cat of CATEGORY_ORDER) {
        const n = counts.get(cat) ?? 0
        if (n > bestCount) {
            best = cat
            bestCount = n
        }
    }
    return best
}
